import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HeartHandshake, Boxes, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/lib/testIds";

// Home screen for both authenticated students (mode="student") and self-serve visitors
// (mode="explore"). Shows two entry cards: "How I'm feeling" and "Sensory boxes".
export default function ChooseEntry({ mode = "student" }) {
  const { actor } = useAuth();
  const nav = useNavigate();
  const base = mode === "student" ? "/play" : "/explore";
  const greeting = mode === "student" && actor?.name
    ? `Hi, ${actor.name.split(" ")[0]}`
    : "Welcome";

  return (
    <div data-testid={mode === "student" ? TID.studentHome : TID.exploreHome} className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">{greeting}</div>
        <h1 className="display mt-1 text-4xl md:text-6xl font-extrabold leading-tight">
          How are you feeling right now?
        </h1>
        <p className="mt-2 text-lg text-[var(--sw-ink-soft)] max-w-2xl">
          Pick a starting point. You can also browse sensory boxes if you&apos;d rather explore by body input.
        </p>
      </header>

      <div className="grid md:grid-cols-2 gap-5 md:gap-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <Link
            to={`${base}/feelings`}
            data-testid={TID.homeChooseFeelings}
            className="block rounded-3xl border-2 p-8 md:p-10 touch-target transition hover:shadow-lg h-full"
            style={{ background: "#FBE8DE", borderColor: "#F1CDBB", minHeight: 220 }}
          >
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl" style={{ background: "rgba(255,255,255,0.65)", color: "#B04A2E" }}>
              <HeartHandshake size={30} strokeWidth={1.8} />
            </div>
            <div className="display text-2xl md:text-3xl font-extrabold mt-5" style={{ color: "#7A3320" }}>
              How I&apos;m feeling
            </div>
            <div className="mt-2 text-base md:text-lg" style={{ color: "#7A3320", opacity: 0.85 }}>
              Start with a feeling — anger, fear, overwhelm, or sadness — and get strategies matched to it.
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "#B04A2E" }}>
              Start here <ArrowRight size={16} />
            </div>
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.1 }}>
          <Link
            to={`${base}/boxes`}
            data-testid={TID.homeChooseBoxes}
            className="block rounded-3xl border-2 p-8 md:p-10 touch-target transition hover:shadow-lg h-full"
            style={{ background: "#EEF3EC", borderColor: "#CDDDD0", minHeight: 220 }}
          >
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl" style={{ background: "rgba(255,255,255,0.65)", color: "#2C4A3B" }}>
              <Boxes size={30} strokeWidth={1.8} />
            </div>
            <div className="display text-2xl md:text-3xl font-extrabold mt-5" style={{ color: "#1F3A2C" }}>
              Sensory boxes
            </div>
            <div className="mt-2 text-base md:text-lg" style={{ color: "#1F3A2C", opacity: 0.85 }}>
              Browse the eight sensory systems and pop a bubble that matches what your body wants right now.
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "#2C4A3B" }}>
              Open the boxes <ArrowRight size={16} />
            </div>
          </Link>
        </motion.div>
      </div>

      {mode === "explore" && (
        <div className="rounded-2xl border border-[var(--sw-line)] bg-[var(--sw-bg-soft)] p-5 text-sm text-[var(--sw-ink-soft)]">
          You&apos;re in <strong className="text-[var(--sw-ink)]">self-serve mode</strong> &mdash; every strategy is visible and your picks stay on this device.
          If your OT has set up an account for you, <button className="underline font-semibold text-[var(--sw-primary)]" onClick={() => nav("/student-login")}>sign in</button> to see the set curated just for you.
        </div>
      )}
    </div>
  );
}
